const shimmerClass = "animate-pulse bg-[linear-gradient(110deg,rgba(255,255,255,0.04)_8%,rgba(255,255,255,0.12)_18%,rgba(255,255,255,0.04)_33%)] bg-[length:200%_100%]";

export const AgentSkeletonBlock = ({ className = "", testId }) => (
  <div className={`rounded-2xl bg-[#1A1A1A] ${shimmerClass} ${className}`.trim()} data-testid={testId} />
);

export const AgentLandingSkeleton = () => (
  <div className="bg-[#05070a] pb-12 text-white" data-testid="agent-landing-skeleton">
    <section className="section-shell py-6">
      <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <div className="space-y-5 rounded-[32px] border border-white/10 bg-[#101214] p-5 sm:p-7">
          <AgentSkeletonBlock className="h-3 w-32" testId="agent-landing-skeleton-kicker" />
          <AgentSkeletonBlock className="h-14 w-full max-w-3xl" testId="agent-landing-skeleton-heading" />
          <AgentSkeletonBlock className="h-5 w-full max-w-2xl" testId="agent-landing-skeleton-subheading-1" />
          <AgentSkeletonBlock className="h-5 w-4/5 max-w-xl" testId="agent-landing-skeleton-subheading-2" />
          <div className="flex flex-wrap gap-3">
            <AgentSkeletonBlock className="h-12 w-40 rounded-full" testId="agent-landing-skeleton-cta-1" />
            <AgentSkeletonBlock className="h-12 w-40 rounded-full" testId="agent-landing-skeleton-cta-2" />
          </div>
        </div>
        <div className="rounded-[32px] border border-white/10 bg-[#101214] p-5 sm:p-7">
          <AgentSkeletonBlock className="aspect-[4/5] w-full rounded-[28px]" testId="agent-landing-skeleton-profile-card" />
        </div>
      </div>
    </section>
    <section className="section-shell py-4">
      <div className="grid gap-4 md:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <AgentSkeletonBlock className="h-32 w-full rounded-[28px]" key={index} testId={`agent-landing-skeleton-stat-${index + 1}`} />
        ))}
      </div>
    </section>
    <section className="section-shell py-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <AgentSkeletonBlock className="h-80 w-full rounded-[32px]" testId="agent-landing-skeleton-panel-left" />
        <AgentSkeletonBlock className="h-80 w-full rounded-[32px]" testId="agent-landing-skeleton-panel-right" />
      </div>
    </section>
  </div>
);

export const AgentListingsSkeleton = () => (
  <div data-testid="agent-listings-skeleton">
    <section className="section-shell">
      <div className="space-y-4">
        <AgentSkeletonBlock className="h-3 w-28" testId="agent-listings-skeleton-kicker" />
        <AgentSkeletonBlock className="h-12 w-full max-w-2xl" testId="agent-listings-skeleton-heading" />
        <AgentSkeletonBlock className="h-5 w-full max-w-3xl" testId="agent-listings-skeleton-copy-1" />
        <AgentSkeletonBlock className="h-5 w-4/5 max-w-2xl" testId="agent-listings-skeleton-copy-2" />
      </div>
      <div className="mt-8 rounded-[30px] border border-white/10 bg-[#101214] p-5 sm:p-6">
        <div className="grid gap-3 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <AgentSkeletonBlock className="h-12 w-full rounded-[18px]" key={index} testId={`agent-listings-skeleton-filter-${index + 1}`} />
          ))}
        </div>
      </div>
    </section>
    <section className="section-shell pt-4">
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <div className="rounded-[30px] border border-white/10 bg-[#101214] p-4" key={index}>
            <AgentSkeletonBlock className="aspect-[1.15/1] w-full rounded-[24px]" testId={`agent-listings-skeleton-card-image-${index + 1}`} />
            <AgentSkeletonBlock className="mt-4 h-3 w-24" testId={`agent-listings-skeleton-card-kicker-${index + 1}`} />
            <AgentSkeletonBlock className="mt-3 h-8 w-4/5" testId={`agent-listings-skeleton-card-title-${index + 1}`} />
            <AgentSkeletonBlock className="mt-3 h-4 w-full" testId={`agent-listings-skeleton-card-copy-${index + 1}`} />
            <AgentSkeletonBlock className="mt-5 h-11 w-full rounded-full" testId={`agent-listings-skeleton-card-cta-${index + 1}`} />
          </div>
        ))}
      </div>
    </section>
  </div>
);

export const AgentListingDetailSkeleton = () => (
  <div className="bg-[#05070a] pb-16 text-white" data-testid="agent-listing-detail-skeleton">
    <section className="relative overflow-hidden">
      <AgentSkeletonBlock className="h-[48vh] w-full rounded-none" testId="agent-listing-detail-skeleton-hero" />
    </section>
    <section className="section-shell pt-6">
      <div className="grid gap-4 md:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <AgentSkeletonBlock className="h-28 w-full rounded-[24px]" key={index} testId={`agent-listing-detail-skeleton-stat-${index + 1}`} />
        ))}
      </div>
      <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-6">
          {Array.from({ length: 4 }).map((_, index) => (
            <AgentSkeletonBlock className="h-72 w-full rounded-[30px]" key={index} testId={`agent-listing-detail-skeleton-section-${index + 1}`} />
          ))}
        </div>
        <AgentSkeletonBlock className="h-[32rem] w-full rounded-[30px]" testId="agent-listing-detail-skeleton-sidebar" />
      </div>
    </section>
  </div>
);

export const AgentDashboardSkeleton = () => (
  <div className="space-y-6" data-testid="agent-dashboard-skeleton">
    <div className="rounded-[32px] border border-[#001935]/10 bg-white/90 p-5 md:p-7">
      <AgentSkeletonBlock className="h-3 w-32 bg-[#efe7d8]" testId="agent-dashboard-skeleton-kicker" />
      <AgentSkeletonBlock className="mt-4 h-12 w-full max-w-2xl bg-[#efe7d8]" testId="agent-dashboard-skeleton-heading" />
      <AgentSkeletonBlock className="mt-3 h-5 w-full max-w-3xl bg-[#efe7d8]" testId="agent-dashboard-skeleton-copy" />
    </div>
    <div className="grid gap-4 md:grid-cols-3">
      {Array.from({ length: 3 }).map((_, index) => (
        <AgentSkeletonBlock className="h-36 w-full rounded-[28px] bg-[#efe7d8]" key={index} testId={`agent-dashboard-skeleton-stat-${index + 1}`} />
      ))}
    </div>
    <div className="grid gap-5 xl:grid-cols-[0.9fr_1.1fr]">
      <AgentSkeletonBlock className="h-[26rem] w-full rounded-[30px] bg-[#efe7d8]" testId="agent-dashboard-skeleton-panel-left" />
      <AgentSkeletonBlock className="h-[26rem] w-full rounded-[30px] bg-[#efe7d8]" testId="agent-dashboard-skeleton-panel-right" />
    </div>
  </div>
);